import { NumberFormatValues } from 'react-number-format';
import { Currency } from '../../types/common';
import { IStrategyAssetTarget } from '../../types/model';
import { Target } from '../Widget/types';
import { useTranslation } from 'react-i18next';
import { CustomNumber } from '../CustomNumber/CustomNumber';
import { numberFormat } from '../../helpers/calc';

export const AssetTargetStop = ({
  assetCurrency,
  reportingCurrency,
  entryPrice,
  entryExchangePrice,
  target,
  targetType,
  title,
  updateTarget,
}: {
  assetCurrency: string;
  reportingCurrency: Currency['code'];
  entryPrice: number;
  entryExchangePrice: number;
  target: Target;
  targetType: 'AMOUNT' | 'PERCENTAGE';
  title: 'Profit' | 'Loss';
  updateTarget(
    key: keyof IStrategyAssetTarget,
    value?: Date | string | number
  ): void;
}) => {
  const { t } = useTranslation();

  const isPercentage = targetType === 'PERCENTAGE';
  const isProfit = title === 'Profit';
  const suffix = isPercentage ? '%' : assetCurrency;
  const minValue = isPercentage
    ? isProfit
      ? 0.01
      : -100
    : isProfit
    ? entryPrice
    : 0.00000000001;

  const maxValue = isPercentage
    ? isProfit
      ? undefined
      : 100
    : isProfit
    ? undefined
    : entryPrice;

  const targetValueInAssetCurrency = target[`stop${title}`][targetType];
  const targetValueInReportingCurrency =
    target[`stop${title}`]['AMOUNT_IN_REPORTING_CURRENCY'];

  const decimalScale = isPercentage
    ? 2
    : targetValueInAssetCurrency === undefined
    ? 11
    : targetValueInAssetCurrency < 1
    ? 11
    : 2;

  const placeholder = `${isPercentage ? (isProfit ? '+10' : '-10') : '1000'}`;

  const prefix = isPercentage
    ? title === 'Profit'
      ? '+'
      : targetValueInAssetCurrency !== undefined &&
        targetValueInAssetCurrency >= 0
      ? '-'
      : undefined
    : undefined;

  const onValueChange = ({ floatValue }: NumberFormatValues) => {
    const key: 'stopLoss' | 'stopProfit' = `stop${title}`;
    updateTarget(key, floatValue!);
  };

  return (
    <div
      key={title}
      className={`
        flex
        flex-col
        w-1/2
        relative
        `}
    >
      <CustomNumber
        allowNegative={isPercentage && !isProfit}
        decimalScale={decimalScale}
        decimalScaleOnFocus={decimalScale}
        placeholder={placeholder}
        prefix={prefix}
        readOnly={false}
        id={title}
        suffix={suffix}
        suffixOverlay={true}
        value={targetValueInAssetCurrency}
        onValueChange={onValueChange}
        showLabel={true}
        fixedLabel={false}
        label={t(`strategies.assetAdvancedSettings.form.label.stop${title}`)}
        validate={(value) =>
          !!value &&
          ((isPercentage && isProfit && value > 0) ||
            (isPercentage && !isProfit && value < 0) ||
            (!isPercentage && value > 0))
        }
      />
      {targetValueInAssetCurrency !== undefined && (
        <div className={'text-xs text-grey-30'}>
          {t('strategies.configureAsset.inReportingCurrency', {
            value: targetValueInReportingCurrency
              ? numberFormat(targetValueInReportingCurrency)
              : 0,
            currency: reportingCurrency,
          })}
        </div>
      )}
      {targetValueInAssetCurrency !== undefined && (
        <div className="text-negative-light-1 text-xs absolute top-full pt-3 flex h-12">
          {isPercentage
            ? isProfit
              ? targetValueInAssetCurrency < minValue &&
                t(`strategies.assetAdvancedSettings.form.error.percentageLte0`)
              : (targetValueInAssetCurrency > maxValue! ||
                  targetValueInAssetCurrency < minValue) &&
                t(
                  `strategies.assetAdvancedSettings.form.error.percentageGte100`
                )
            : isProfit
            ? targetValueInAssetCurrency < minValue &&
              t(`strategies.assetAdvancedSettings.form.error.valueLtEntryPrice`)
            : targetValueInAssetCurrency > maxValue! &&
              t(
                `strategies.assetAdvancedSettings.form.error.valueGtEntryPrice`
              )}
        </div>
      )}
    </div>
  );
};
